import { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { invalidateAfterCodeMutation } from 'core/api/invalidation';
import { queryKeys } from 'core/api/queryKeys';
import { humanizeApiError } from 'core/errors/mapFieldErrors';
import { useI18n } from 'core/i18n/I18nProvider';
import { companyApi } from 'features/company/company.api';
import { useCompany } from 'features/company/CompanyContext';
import { canRevokeCode } from 'features/company/companyUtils';
import { CodeGenerationDialog } from 'features/company/CodeGenerationDialog';
import { EmptyState, ErrorState, PageHeader, Skeleton, StatusChip } from 'shared/components/Feedback';
import { PageMeta } from 'shared/components/PageMeta';
import { Pagination } from 'shared/components/Pagination';
import { useConfirm } from 'shared/components/ConfirmDialog';
import { formatDateTime } from 'shared/utilities/format';

export function CompanyCodesPage() {
  const { t, locale } = useI18n();
  const { companyId, permissions } = useCompany();
  const confirm = useConfirm();
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState('');
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState('');

  const filters = useMemo(() => ({ page, per_page: 15, status: status || undefined }), [page, status]);
  const query = useQuery({
    queryKey: queryKeys.companyCodes(filters),
    queryFn: () => companyApi.codes(companyId, filters),
  });

  const handleRevoke = async (code) => {
    if (!canRevokeCode(code)) {
      return;
    }
    const confirmed = await confirm({
      title: t('company.codes.revokeTitle'),
      body: t('company.codes.revokeBody'),
      confirmLabel: t('company.codes.revokeConfirm'),
      tone: 'danger',
    });
    if (!confirmed) {
      return;
    }
    try {
      await companyApi.revokeCode(companyId, code.id);
      await invalidateAfterCodeMutation(queryClient);
    } catch (error) {
      setMessage(humanizeApiError(error, t));
    }
  };

  if (query.isLoading) {
    return <Skeleton rows={8} />;
  }
  if (query.isError) {
    return <ErrorState title={humanizeApiError(query.error, t)} onRetry={() => query.refetch()} retryLabel={t('actions.retry')} />;
  }

  const items = query.data?.items || [];
  return (
    <div className="company-codes-page">
      <PageMeta titleKey="meta.companyCodes" />
      <PageHeader
        title={t('company.codes.title')}
        description={t('company.codes.body')}
        actions={permissions.canManageCodes ? (
          <button type="button" className="btn btn-primary" onClick={() => setOpen(true)}>
            {t('company.codes.generate')}
          </button>
        ) : null}
      />
      <div className="filter-row">
        <select className="input" value={status} onChange={(event) => { setStatus(event.target.value); setPage(1); }}>
          <option value="">{t('company.codes.allStatuses')}</option>
          <option value="active">{t('company.codes.statuses.active')}</option>
          <option value="claimed">{t('company.codes.statuses.claimed')}</option>
          <option value="completed">{t('company.codes.statuses.completed')}</option>
          <option value="revoked">{t('company.codes.statuses.revoked')}</option>
          <option value="expired">{t('company.codes.statuses.expired')}</option>
        </select>
      </div>
      {message ? <p className="field-error" role="alert">{message}</p> : null}
      {items.length === 0 ? (
        <EmptyState title={t('company.codes.empty')} />
      ) : (
        <div className="record-table">
          {items.map((code) => (
            <article key={code.id} className="record-row">
              <div>
                <strong dir="ltr">{code.code_masked}</strong>
                <StatusChip>{code.status_label || t(`company.codes.statuses.${code.status}`)}</StatusChip>
              </div>
              <p>{code.batch_label || code.batch?.label || '—'}</p>
              <p className="muted">{formatDateTime(code.created_at, locale)}</p>
              <p>{code.participant_name || code.claimed_by?.name || '—'}</p>
              <div className="action-row">
                {permissions.canManageCodes && canRevokeCode(code) ? (
                  <button type="button" className="btn btn-danger" onClick={() => { void handleRevoke(code); }}>
                    {t('company.codes.revoke')}
                  </button>
                ) : null}
              </div>
            </article>
          ))}
        </div>
      )}
      <Pagination meta={query.data?.meta} page={page} onPageChange={setPage} />
      <CodeGenerationDialog open={open} onClose={() => setOpen(false)} />
    </div>
  );
}
